import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';

import { selectOrganisationInfos } from '@/domain/organisation/organisation.selectors';
import { ProjectTour } from '@/domain/project-tour/entities/project-tour.entity';
import { FSDateField } from '@/ui/components/input/FSDateField';
import { FSImageField } from '@/ui/components/input/FSImageField';
import { UploadImageServiceS3 } from '@/ui/components/input/FSImageField.utils/upload-image-service-s3';
import { FSTextField } from '@/ui/components/input/FSTextField';
import { useAppSelector } from '@/ui/hook/store';
import { translate } from '@/ui/i18n';
import { isAtLeastStartDate } from '@/ui/utils/form-validations';

export function ProjectTourForm({
  project,
  buttons,
  onSubmit,
}: {
  project: ProjectTour;
  buttons?: React.ReactNode;
  onSubmit: (data: ProjectTour) => void;
}) {
  const organisationId = useAppSelector(selectOrganisationInfos).id.toString();
  const imageDestinationFolder = organisationId;

  const { handleSubmit, register, formState, control, getValues, watch } =
    useForm<ProjectTour>({
      defaultValues: project,
    });

  const { errors } = formState;

  const handleFormSubmit = (data: ProjectTour) => {
    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <Stack>
        <Controller
          name="image"
          control={control}
          render={({ field }) => (
            <FSImageField
              name={field.name}
              value={field.value}
              onChange={field.onChange}
              uploadImageService={
                new UploadImageServiceS3(imageDestinationFolder)
              }
            />
          )}
        />
        <FSTextField
          label={translate('ProjectsTour.Form.project-name')}
          defaultValue={project.name}
          register={{
            ...register('name', {
              required: translate('Common.name-is-required'),
            }),
          }}
          error={!!errors.name}
          helperText={errors.name?.message}
        />
        <Stack sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <FSDateField
            sx={{ width: '100%' }}
            value={project.startDate || ''}
            label={translate('Form.start-date')}
            control={control}
            name="startDate"
            error={!!errors.startDate}
            helperText={errors.startDate?.message}
          />
          <FSDateField
            sx={{ width: '100%' }}
            value={project.endDate || ''}
            label={translate('Form.end-date')}
            minDate={dayjs(watch('startDate'))}
            control={control}
            name="endDate"
            error={!!errors.endDate}
            helperText={errors.endDate?.message}
            validate={(date) =>
              isAtLeastStartDate(date, getValues('startDate') || '')
            }
          />
        </Stack>
        {buttons && buttons}
      </Stack>
    </form>
  );
}
